import React, { useState, useRef, useEffect } from 'react';
import { insurerService, BulkInviteStatusResponse, PartnerTokenTier } from '../../services/insurerService';

interface BulkInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskStarted?: (taskId: string) => void;
  grantCredit?: boolean;
  tokenTier?: PartnerTokenTier;
}

const BulkInviteModal: React.FC<BulkInviteModalProps> = ({
  isOpen,
  onClose,
  onTaskStarted,
  grantCredit = true,
  tokenTier = 'simple',
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [status, setStatus] = useState<BulkInviteStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef(0);
  const MAX_POLL_ATTEMPTS = 150;

  useEffect(() => {
    if (taskId && isOpen) {
      pollAttemptsRef.current = 0;
      statusIntervalRef.current = setInterval(async () => {
        pollAttemptsRef.current += 1;
        if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
          if (statusIntervalRef.current) {
            clearInterval(statusIntervalRef.current);
            statusIntervalRef.current = null;
          }
          setIsUploading(false);
          setError(
            'Processing timed out. The background worker may be down — retry later or contact support.'
          );
          return;
        }
        try {
          const statusData = await insurerService.getBulkInviteStatus(taskId);
          setStatus(statusData);

          if (statusData.status === 'completed' || statusData.status === 'failed') {
            if (statusIntervalRef.current) {
              clearInterval(statusIntervalRef.current);
              statusIntervalRef.current = null;
            }
            setIsUploading(false);
          }
        } catch (err: any) {
          console.error('Error fetching status:', err);
        }
      }, 2000);
    }

    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, [taskId, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setTaskId(null);
      setStatus(null);
      setError(null);
      setIsUploading(false);
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file: File) => {
    // Validate file type
    const validTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/vnd.ms-excel'];
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setError('Invalid file type. Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      const response = await insurerService.bulkInviteClients(file, grantCredit, tokenTier);

      // Notify parent component if callback provided (for showing status on main page)
      if (onTaskStarted) {
        onTaskStarted(response.task_id);
        // Close modal and let parent handle status display
        onClose();
        return;
      }

      // Otherwise, handle status in modal
      setTaskId(response.task_id);

      // Immediately fetch initial status
      const initialStatus = await insurerService.getBulkInviteStatus(response.task_id);
      setStatus(initialStatus);
    } catch (err: any) {
      setError(err.message || 'Failed to upload file');
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block w-full max-w-2xl p-6 my-8 text-left align-middle bg-white rounded-2xl shadow-xl transition-all transform">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Bulk Invite Clients</h3>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {!taskId && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Upload a CSV or Excel file containing client email addresses. Each email will receive an invitation to join Flash AI™.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>File Format:</strong> Your file should have an "email" column containing valid email addresses.
                    Other columns will be ignored.
                  </p>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 text-gray-400">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-900">
                      Drop your file here, or{' '}
                      <button
                        onClick={handleClick}
                        className="text-blue-600 hover:text-blue-500 underline"
                        disabled={isUploading}
                      >
                        browse
                      </button>
                    </p>
                    <p className="text-sm text-gray-500 mt-2">CSV or Excel files (.csv, .xlsx, .xls)</p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isUploading}
                />
              </div>
            </div>
          )}

          {taskId && status && (
            <div className="space-y-6">
              <div className="text-center">
                <h4 className="text-lg font-medium text-gray-900 mb-2">Processing Invitations</h4>
                <p className="text-sm text-gray-600">
                  {status.status === 'processing' && `Batch ${status.batches_completed} of ${status.total_batches}`}
                  {status.status === 'completed' && 'All invitations processed'}
                  {status.status === 'failed' && 'Processing failed'}
                </p>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${status.progress}%` }}
                ></div>
              </div>

              {/* Results */}
              {status.status === 'completed' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{status.successful}</div>
                      <div className="text-sm text-green-800">Successful</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-red-600">{status.failed}</div>
                      <div className="text-sm text-red-800">Failed</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600">{status.skipped}</div>
                      <div className="text-sm text-yellow-800">Skipped</div>
                    </div>
                  </div>

                  {status.skipped_details && status.skipped_details.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                      <h5 className="font-medium text-yellow-900 mb-2">Skipped:</h5>
                      <ul className="text-sm text-yellow-900 space-y-1">
                        {status.skipped_details.slice(0, 15).map((row, index) => (
                          <li key={index}>
                            {row.email}: {row.reason.replace(/_/g, ' ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {status.errors && status.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                      <h5 className="font-medium text-red-900 mb-2">Errors:</h5>
                      <ul className="text-sm text-red-800 space-y-1">
                        {status.errors.slice(0, 10).map((error, index) => (
                          <li key={index}>
                            {error.email && `${error.email}: `}
                            {error.error}
                          </li>
                        ))}
                        {status.errors.length > 10 && (
                          <li className="text-gray-600">... and {status.errors.length - 10} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {status.status === 'failed' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    {status.errors && status.errors.length > 0
                      ? status.errors[0].error
                      : 'An error occurred while processing invitations'}
                  </p>
                </div>
              )}
            </div>
          )}

          {isUploading && !status && (
            <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-2xl">
              <div className="text-center">
                <div className="mx-auto mb-4 w-8 h-8 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
                <p className="text-gray-600">Uploading file...</p>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {status?.status === 'completed' || status?.status === 'failed' ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkInviteModal;

